import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  approveBlogPost,
  deleteBlogPost,
  draftBlogPost,
  editorListBlogPosts,
  myBlogRole,
  rejectBlogPost,
  saveBlogPost,
} from "@/lib/blog.functions";
import {
  BLOG_TAGS,
  STATUS_LABEL,
  readingMinutes,
  slugifyTitle,
  upsertPostSchema,
  type BlogPost,
  type BlogStatus,
} from "@/lib/blog-shared";
import { BlogBody } from "@/components/BlogBody";
import { toast } from "sonner";

export const Route = createFileRoute("/blog-admin")({
  head: () => ({
    meta: [
      { title: "Blog Console — Pal Stars Staff" },
      { name: "description", content: "Private Pal Stars blog console: write, AI-draft, preview, submit for approval and publish Palworld articles." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Pal Stars blog console" },
      { property: "og:description", content: "Private staff console for Palworld blog articles." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: BlogAdmin,
});

interface Draft {
  id?: string;
  slug: string;
  title: string;
  excerpt: string;
  body: string;
  cover_image: string;
  cover_alt: string;
  tag: string;
  author: string;
  published: boolean;
  status: BlogStatus;
}

const EMPTY: Draft = {
  slug: "",
  title: "",
  excerpt: "",
  body: "",
  cover_image: "",
  cover_alt: "",
  tag: BLOG_TAGS[0],
  author: "Pal Stars",
  published: false,
  status: "draft",
};

const CHEATSHEET = [
  "## Section heading   ### Sub-heading",
  "- bullet   1. numbered   > pull quote   --- divider",
  "**bold**  *italic*  `code`  [text](/prices)  [buy](https://www.ebay.com/…)",
  "![alt text](https://image-url.jpg)   paste a YouTube URL on its own line to embed",
];

const STATUS_TONE: Record<BlogStatus, string> = {
  draft: "text-muted-foreground",
  pending: "text-amber-400",
  published: "text-emerald-400",
  rejected: "text-destructive",
};

function BlogAdmin() {
  const qc = useQueryClient();
  const roleFn = useServerFn(myBlogRole);
  const listFn = useServerFn(editorListBlogPosts);
  const saveFn = useServerFn(saveBlogPost);
  const deleteFn = useServerFn(deleteBlogPost);
  const draftFn = useServerFn(draftBlogPost);
  const approveFn = useServerFn(approveBlogPost);
  const rejectFn = useServerFn(rejectBlogPost);

  const role = useQuery({ queryKey: ["my-blog-role"], queryFn: () => roleFn(), retry: false });
  const isAdmin = role.data?.admin === true;
  const isWriter = role.data?.writer === true;
  const canEdit = isAdmin || isWriter;

  const { data: posts, isLoading, error } = useQuery({
    queryKey: ["editor-blog-posts"],
    queryFn: () => listFn(),
    retry: false,
    enabled: canEdit,
  });

  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [slugLocked, setSlugLocked] = useState(false);
  const [topic, setTopic] = useState("");
  const [angle, setAngle] = useState("");
  const [preview, setPreview] = useState(true);

  useEffect(() => {
    if (!slugLocked && draft.title && !draft.id) {
      setDraft((d) => ({ ...d, slug: slugifyTitle(d.title) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.title, slugLocked, draft.id]);

  function refresh() {
    void qc.invalidateQueries({ queryKey: ["editor-blog-posts"] });
    void qc.invalidateQueries({ queryKey: ["blog-posts"] });
  }

  const save = useMutation({
    mutationFn: async (input: Draft & { status: BlogStatus }) => {
      const parsed = upsertPostSchema.parse({
        ...(input.id ? { id: input.id } : {}),
        slug: input.slug,
        title: input.title,
        excerpt: input.excerpt,
        body: input.body,
        cover_image: input.cover_image.trim() || null,
        cover_alt: input.cover_alt.trim() || null,
        tag: input.tag,
        author: input.author,
        published: isAdmin ? input.published : false,
        status: input.status,
      });
      return saveFn({ data: parsed });
    },
    onSuccess: (row) => {
      toast.success(
        row.published ? "Published" : row.status === "pending" ? "Submitted for approval" : "Draft saved",
      );
      setDraft((d) => ({ ...d, id: row.id, status: row.status, published: row.published }));
      refresh();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Post deleted");
      setDraft(EMPTY);
      refresh();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Delete failed"),
  });

  const approve = useMutation({
    mutationFn: (id: string) => approveFn({ data: { id } }),
    onSuccess: (row) => {
      toast.success("Approved and published — pushed to search and news crawlers");
      setDraft((d) => (d.id === row.id ? { ...d, status: row.status, published: row.published } : d));
      refresh();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Approval failed"),
  });

  const reject = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => rejectFn({ data: { id, note } }),
    onSuccess: () => {
      toast.success("Sent back to the writer");
      refresh();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Could not send it back"),
  });

  const aiDraft = useMutation({
    mutationFn: () => draftFn({ data: { topic, angle: angle || undefined, tag: draft.tag } }),
    onSuccess: (res) => {
      setDraft((d) => ({
        ...d,
        title: res.title || d.title,
        excerpt: res.excerpt || d.excerpt,
        body: res.body || d.body,
        slug: d.id ? d.slug : slugifyTitle(res.title || d.title),
      }));
      toast.success("AI draft inserted — review before submitting");
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Drafting failed"),
  });

  const words = useMemo(() => draft.body.trim().split(/\s+/).filter(Boolean).length, [draft.body]);
  const pending = useMemo(() => (posts ?? []).filter((p) => p.status === "pending"), [posts]);

  function edit(p: BlogPost) {
    setDraft({
      id: p.id,
      slug: p.slug,
      title: p.title,
      excerpt: p.excerpt ?? "",
      body: p.body ?? "",
      cover_image: p.cover_image ?? "",
      cover_alt: p.cover_alt ?? "",
      tag: p.tag,
      author: p.author,
      published: p.published,
      status: p.status,
    });
    setSlugLocked(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  if (role.isLoading) {
    return <div className="mx-auto max-w-3xl px-4 py-16 text-center text-sm text-muted-foreground">Checking your access…</div>;
  }

  if (role.error || !canEdit) {
    const signedOut = role.error instanceof Error;
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <h1 className="text-2xl font-bold">Blog console</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          {signedOut
            ? "Sign in with your Pal Stars staff account to open the blog console."
            : "This console is limited to admins and assigned writers."}
        </p>
        <div className="mt-5 flex justify-center gap-3">
          <Link to="/auth" className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
            Sign in
          </Link>
          <Link to="/blog" className="rounded-lg border border-border/60 px-4 py-2 text-sm font-semibold">
            Back to the blog
          </Link>
        </div>
      </div>
    );
  }

  const field = "w-full rounded-lg border border-border/60 bg-background/60 px-3 py-2 text-sm";

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10 space-y-8">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">
          Pal Stars · {isAdmin ? "admin" : "writer"}
        </p>
        <h1 className="mt-2 text-3xl font-extrabold">Blog console</h1>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          {isAdmin
            ? "Write or review Palworld articles, approve writer submissions and publish. Publishing pushes the article to search and news crawlers automatically."
            : "Write Palworld articles with images, videos and affiliate links, preview them, then submit for approval. An admin reviews every article before it goes live."}{" "}
          eBay links in the body are EPN-tagged automatically and the disclosure is added for you.
        </p>
        {error ? (
          <p className="mt-2 text-sm text-destructive">
            {error instanceof Error ? error.message : "Could not load your posts."}
          </p>
        ) : null}
      </header>

      {isAdmin && pending.length > 0 ? (
        <section className="glass rounded-2xl border border-amber-500/30 p-5">
          <h2 className="text-lg font-bold">Awaiting your approval ({pending.length})</h2>
          <ul className="mt-3 space-y-2">
            {pending.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border/60 p-3">
                <div>
                  <p className="text-sm font-semibold">{p.title}</p>
                  <p className="text-xs text-muted-foreground">
                    /{p.slug} · {p.tag} · submitted {p.submitted_at ? new Date(p.submitted_at).toLocaleString() : "—"}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => edit(p)} className="rounded-lg border border-border/60 px-3 py-1.5 text-xs font-semibold">
                    Review
                  </button>
                  <button
                    onClick={() => approve.mutate(p.id)}
                    disabled={approve.isPending}
                    className="rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground disabled:opacity-50"
                  >
                    Approve &amp; publish
                  </button>
                  <button
                    onClick={() => {
                      const note = prompt("What needs changing?") ?? "";
                      reject.mutate({ id: p.id, note });
                    }}
                    className="rounded-lg border border-destructive/60 px-3 py-1.5 text-xs font-semibold text-destructive"
                  >
                    Request changes
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="glass rounded-2xl p-5">
        <h2 className="text-lg font-bold">AI first draft</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-[2fr_3fr_auto]">
          <input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Topic — e.g. Is a Dawn of Palpagos booster box worth it in 2026?"
            className={field}
          />
          <input
            value={angle}
            onChange={(e) => setAngle(e.target.value)}
            placeholder="Angle / must cover (optional)"
            className={field}
          />
          <button
            onClick={() => aiDraft.mutate()}
            disabled={topic.trim().length < 4 || aiDraft.isPending}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >
            {aiDraft.isPending ? "Writing…" : "Draft with AI"}
          </button>
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-[3fr_2fr]">
        <section className="glass rounded-2xl p-5 space-y-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-lg font-bold">{draft.id ? "Edit post" : "New post"}</h2>
            <span className={`text-xs font-semibold ${STATUS_TONE[draft.status]}`}>{STATUS_LABEL[draft.status]}</span>
          </div>

          <label className="block text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Title
            <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className={`mt-1 ${field}`} />
          </label>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Slug
              <input
                value={draft.slug}
                onChange={(e) => {
                  setSlugLocked(true);
                  setDraft({ ...draft, slug: slugifyTitle(e.target.value) });
                }}
                className={`mt-1 ${field}`}
              />
            </label>
            <label className="block text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Category
              <select value={draft.tag} onChange={(e) => setDraft({ ...draft, tag: e.target.value })} className={`mt-1 ${field}`}>
                {BLOG_TAGS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="block text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Excerpt / meta description
            <textarea
              value={draft.excerpt}
              onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })}
              rows={2}
              maxLength={300}
              className={`mt-1 ${field}`}
            />
          </label>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Cover image URL
              <input value={draft.cover_image} onChange={(e) => setDraft({ ...draft, cover_image: e.target.value })} className={`mt-1 ${field}`} />
            </label>
            <label className="block text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Cover alt text
              <input value={draft.cover_alt} onChange={(e) => setDraft({ ...draft, cover_alt: e.target.value })} className={`mt-1 ${field}`} />
            </label>
          </div>

          <label className="block text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Body ({words} words · {readingMinutes(draft.body)} min read)
            <textarea
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              rows={22}
              className={`mt-1 ${field} font-mono text-[13px]`}
            />
          </label>

          <ul className="space-y-1 text-[11px] text-muted-foreground">
            {CHEATSHEET.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>

          <div className="flex flex-wrap items-center gap-3 pt-2">
            {isAdmin ? (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={draft.published}
                  onChange={(e) => setDraft({ ...draft, published: e.target.checked })}
                />
                Published
              </label>
            ) : null}

            <button
              onClick={() => save.mutate({ ...draft, status: draft.published && isAdmin ? "published" : "draft" })}
              disabled={save.isPending}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              {save.isPending ? "Saving…" : isAdmin && draft.published ? "Save & publish" : "Save draft"}
            </button>

            {!isAdmin ? (
              <button
                onClick={() => save.mutate({ ...draft, status: "pending" })}
                disabled={save.isPending || draft.title.length < 5}
                className="rounded-lg border border-primary/60 px-3 py-2 text-sm font-semibold text-primary disabled:opacity-50"
              >
                Submit for approval
              </button>
            ) : null}

            <button onClick={() => setPreview((p) => !p)} className="rounded-lg border border-border/60 px-3 py-2 text-sm">
              {preview ? "Hide preview" : "Show preview"}
            </button>
            <button
              onClick={() => {
                setDraft(EMPTY);
                setSlugLocked(false);
              }}
              className="rounded-lg border border-border/60 px-3 py-2 text-sm"
            >
              New post
            </button>
            {draft.id ? (
              <button
                onClick={() => {
                  if (draft.id && confirm("Delete this post permanently?")) remove.mutate(draft.id);
                }}
                className="rounded-lg border border-destructive/60 px-3 py-2 text-sm text-destructive"
              >
                Delete
              </button>
            ) : null}
            {isAdmin && draft.id && !draft.published ? (
              <button
                onClick={() => draft.id && approve.mutate(draft.id)}
                disabled={approve.isPending}
                className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-bold text-white disabled:opacity-50"
              >
                Approve &amp; publish
              </button>
            ) : null}
            {draft.id && draft.published ? (
              <Link to="/blog/$slug" params={{ slug: draft.slug }} className="text-sm font-semibold text-primary hover:underline">
                View live →
              </Link>
            ) : null}
          </div>

          {draft.status === "rejected" ? (
            <p className="rounded-lg border border-destructive/40 p-3 text-xs text-destructive">
              Changes requested by the admin. Update the article and submit it again.
            </p>
          ) : null}
        </section>

        <div className="space-y-6">
          {preview ? (
            <section className="glass rounded-2xl p-5">
              <h2 className="text-lg font-bold">Preview</h2>
              <div className="mt-3">
                {draft.cover_image ? (
                  <img
                    src={draft.cover_image}
                    alt={draft.cover_alt || draft.title}
                    loading="lazy"
                    className="mb-4 aspect-[16/9] w-full rounded-xl border border-border/60 object-cover"
                  />
                ) : null}
                <h3 className="text-2xl font-bold">{draft.title || "Untitled article"}</h3>
                <div className="mt-3">
                  <BlogBody body={draft.body} />
                </div>
              </div>
            </section>
          ) : null}

          <section className="glass rounded-2xl p-5">
            <h2 className="text-lg font-bold">{isAdmin ? "All posts" : "Your posts"}</h2>
            {isLoading ? (
              <p className="mt-2 text-sm text-muted-foreground">Loading…</p>
            ) : (posts ?? []).length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">No posts yet.</p>
            ) : (
              <ul className="mt-3 space-y-2">
                {(posts ?? []).map((p: BlogPost) => (
                  <li key={p.id} className="rounded-lg border border-border/60 p-3">
                    <p className="text-sm font-semibold">{p.title}</p>
                    <p className="text-xs text-muted-foreground">
                      /{p.slug} · {p.tag} · <span className={STATUS_TONE[p.status]}>{STATUS_LABEL[p.status]}</span>
                    </p>
                    {p.review_note ? <p className="mt-1 text-xs text-destructive">Note: {p.review_note}</p> : null}
                    <button onClick={() => edit(p)} className="mt-2 text-xs font-semibold text-primary hover:underline">
                      Edit →
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
